import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ReactNode } from 'react';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

import Layout from '@components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ChatPage from './pages/ChatPage';
import SharedPage from './pages/SharedPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage';
import AdminConfigPage from './pages/AdminConfigPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import RatingsPage from './pages/RatingsPage';
import AdminRatingsPage from './pages/AdminRatingsPage';

function PermGuard({ permission, children }: { permission: string; children: ReactNode }) {
  const { hasPermission, isLoading } = useAuth();
  if (isLoading) return null;
  if (!hasPermission(permission)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function ProtectedRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gray-200 border-t-[#EDAA00] rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-4">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={<PermGuard permission="dashboard.ver"><DashboardPage /></PermGuard>} />
        <Route path="/chat" element={<PermGuard permission="agente.ver"><ChatPage /></PermGuard>} />
        <Route path="/history" element={<PermGuard permission="historial.ver"><HistoryPage /></PermGuard>} />
        <Route path="/shared" element={<PermGuard permission="compartidos.ver"><SharedPage /></PermGuard>} />
        <Route path="/ratings" element={<PermGuard permission="calificaciones.ver"><RatingsPage /></PermGuard>} />
        <Route path="/settings" element={<PermGuard permission="config.ver"><SettingsPage /></PermGuard>} />
        <Route path="/admin/users" element={<PermGuard permission="usuarios.ver"><UsersPage /></PermGuard>} />
        <Route path="/admin/roles" element={<PermGuard permission="roles.ver"><RolesPage /></PermGuard>} />
        <Route path="/admin/config" element={<PermGuard permission="admin_config.ver"><AdminConfigPage /></PermGuard>} />
        <Route path="/admin/dashboard" element={<PermGuard permission="admin_dashboard.ver"><AdminDashboardPage /></PermGuard>} />
        <Route path="/admin/ratings" element={<PermGuard permission="feedback.ver"><AdminRatingsPage /></PermGuard>} />
        <Route path="/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/roles" element={<Navigate to="/admin/roles" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>Asistente de Apoyo Financiero | Universidad de los Andes</title>
        <meta name="description" content="Asistente inteligente para análisis de solicitudes y acuerdos de nivel de servicio - Unidad de Matrículas y Apoyo Financiero" />
        <meta name="theme-color" content="#1B2A4A" />
      </Helmet>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <ProtectedRoutes />
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
